<template>
  <Dialog 
    :visible="visible" 
    @update:visible="$emit('update:visible', $event)"
    :header="$t('user_manage.invite_member')" 
    :modal="true" 
    class="w-full max-w-4xl"
  >
    <TabView v-model:activeIndex="currentUserTab">
      <TabPanel :header="$t('user_manage.single_invitation')">
        <div class="space-y-4">
          <!-- Service Selection for Single Invitation -->
          <div class="mb-4">
            <label class="block text-sm font-medium mb-2">{{ $t('user_manage.select_service') }}</label>
            <Dropdown 
              v-model="selectedService" 
              :options="computedService" 
              optionLabel="name" 
              optionValue="id" 
              :placeholder="$t('user_manage.choose_service')"
              class="w-full"
            />
          </div>
          
          <div v-for="(email, index) in emailArray" :key="index" class="flex gap-4 p-4 border rounded">
            <div class="flex-1">
              <label class="block text-sm font-medium mb-1">{{ $t('user_manage.email_label') }}</label>
              <InputText v-model="emailArray[index]" type="email" class="w-full" :placeholder="$t('user_manage.enter_email')" />
            </div>
            <div class="flex items-end gap-2">
              <Button 
                :label="$t('user_manage.add_email')"
                icon="pi pi-plus"
                @click="addEmail(index)"
                class="p-button-sm spect-btn-outline"
                outlined
              />
              <Button 
                :label="$t('user_manage.remove_email')"
                icon="pi pi-minus"
                @click="removeEmail(index)"
                class="p-button-sm p-button-danger"
                outlined
                :disabled="emailArray.length === 1"
              />
            </div>
          </div>
        </div>
      </TabPanel>
      
      <TabPanel :header="$t('user_manage.bulk_invitation')">
        <div class="space-y-4">
          <div class="flex gap-2 mb-4">
            <Button 
              :label="$t('user_manage.download_template')"
              icon="pi pi-download"
              @click="downloadTemplate"
              class="p-button-sm p-button-outlined spect-btn-outline"
            />
          </div>
          
          <div>
            <label class="block text-sm font-medium mb-2">{{ $t('user_manage.upload_csv') }}</label>
            <FileUpload 
              mode="basic" 
              accept=".csv" 
              :maxFileSize="1000000" 
              @select="handleFileUpload" 
              :auto="true"
              :chooseLabel="$t('user_manage.choose_csv_file')"
              class="spect-btn-primary"
            />
          </div>
          
          <div v-if="tableData.length > 0">
            <h4 class="font-semibold mb-2">{{ $t('user_manage.preview_data') }}</h4>
            <DataTable :value="tableData" class="p-datatable-sm" :paginator="true" :rows="5">
              <!-- <Column :field="tableField.firstname.field" :header="$t('user_manage.firstname')"></Column>
              <Column :field="tableField.lastname.field" :header="$t('user_manage.lastname')"></Column> -->
              <Column :field="tableField.email.field" :header="$t('user_manage.email_label')"></Column>
              <!-- <Column :field="tableField.phone.field" :header="$t('user_manage.phone_label')"></Column> -->
              <Column :field="tableField.service.field" :header="$t('user_manage.service')"></Column>
              <Column :header="$t('common.actions')">
                <template #body="slotProps">
                  <Button 
                    icon="pi pi-trash" 
                    class="p-button-rounded p-button-text p-button-danger p-button-sm"
                    @click="deleteRow(slotProps.index)"
                  />
                </template>
              </Column>
            </DataTable>
            <Button 
              :label="$t('user_manage.reset_data')"
              icon="pi pi-refresh"
              @click="resetTableData"
              class="mt-2 p-button-sm p-button-secondary spect-btn-outline"
              outlined
            />
          </div>
        </div>
      </TabPanel>
    </TabView>
    
    <template #footer>
      <div class="flex gap-2">
        <Button :label="$t('common.cancel')" @click="closeModal" class="p-button-text primary-text spect-btn-text" />
        <Button 
          :label="$t('user_manage.send_invitations')" 
          @click="sendInvitation" 
          class="spect-btn-primary" 
          :disabled="!isFormValid"
        />
      </div>
    </template>
  </Dialog>
</template>

<script setup>
/* eslint-disable */
import { ref, computed, defineProps, defineEmits, watch } from 'vue'
import Dialog from 'primevue/dialog'
import TabView from 'primevue/tabview'
import TabPanel from 'primevue/tabpanel'
import InputText from 'primevue/inputtext'
import Button from 'primevue/button'
import Dropdown from 'primevue/dropdown'
import FileUpload from 'primevue/fileupload'
import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
//import { useI18n } from 'vue-i18n'

//const { t } = useI18n()

const props = defineProps({
  visible: {
    type: Boolean,
    default: false
  },
  services: {
    type: Array,
    default: () => []
  }
})

const computedService = computed(() => {
  return props.services.map(service => ({
    ...service,
    name: service.name.split('_')[0].charAt(0).toUpperCase() + service.name.split('_')[0].slice(1),
  }))
})

const emit = defineEmits(['update:visible', 'save'])

// Local reactive data
const currentUserTab = ref(0)
const emailArray = ref([''])
const tableData = ref([])
const tableField = computed(()=> {
const language = localStorage.getItem('language') || 'fr'
return language === 'fr' ? {
  //firstname : { field: 'prénom', header: 'Prénom' },
  //lastname: { field: 'nom', header: 'Nom' },
  email: { field: 'email', header: 'E-mail' },
  //phone: { field: 'téléphone', header: 'Téléphone' },
  service : { field: 'service', header: 'Service' }
 } : {
  //firstname : { field: 'firstname', header: 'First Name' },
  //lastname:  { field: 'lastname', header: 'Last Name' },
  email:  { field: 'email', header: 'Email' },
  //phone:  { field: 'phone', header: 'Phone' },
  service : { field: 'service', header: 'Service' }
 }
})
const selectedService = ref(null)

// Computed properties
const isFormValid = computed(() => {
  if (currentUserTab.value === 0) {
    // Single invitation tab - check if at least one email is valid and service is selected
    return emailArray.value.some(email => email && email.includes('@')) && selectedService.value
  } else {
    // Bulk invitation tab - check if table has data
    return tableData.value.length > 0
  }
})

// Methods
const addEmail = (index) => {
  emailArray.value.splice(index + 1, 0, '')
}

const removeEmail = (index) => {
  if (emailArray.value.length > 1) {
    emailArray.value.splice(index, 1)
  }
}

/*const downloadEmailTemplate = () => {
  const csvContent = 'email\nexample1@company.com\nexample2@company.com\nexample3@company.com'
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
  const link = document.createElement('a')
  const url = URL.createObjectURL(blob)
  link.setAttribute('href', url)
  link.setAttribute('download', 'email_invitation_template.csv')
  link.style.visibility = 'hidden'
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}*/

const downloadTemplate = () => {
  const language = localStorage.getItem('language') || 'fr';
  // Add BOM marker for UTF-8 encoding to handle French characters correctly
  const BOM = '\ufeff';
  const csvContent = language === 'fr'
    ? `${BOM}email,service\njohn.doe@example.com,Service1\njane.smith@example.com,Service2`
    : `${BOM}email,service\njohn.doe@example.com,Service1\njane.smith@example.com,Service2`
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8' })
  const link = document.createElement('a')
  const url = URL.createObjectURL(blob)
  link.setAttribute('href', url)
  link.setAttribute('download', `${language === 'fr' ? 'invitation_template_fr.csv' : 'invitation_template_en.csv'}`)
  link.style.visibility = 'hidden'
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}

/*const handleFileUpload = (event) => {
  const file = event.files[0]
  if (file) {
    const reader = new FileReader()
    reader.onload = (e) => {
      const csv = e.target.result
      const lines = csv.split('\n')
      
      const data = []
      for (let i = 1; i < lines.length; i++) {
        if (lines[i].trim()) {
          const email = lines[i].trim()
          if (email.includes('@')) {
            data.push({ email })
          }
        }
      }
      tableData.value = data
    }
    reader.readAsText(file)
  }
}
*/

const handleFileUpload = (event) => {
  const file = event.files[0]
  if (file) {
    const reader = new FileReader()
    reader.onload = (e) => {
      const csv = e.target.result
      const lines = csv.split('\n')
      const headers = lines[0].split(',')
      
      const data = []
      for (let i = 1; i < lines.length; i++) {
        if (lines[i].trim()) {
          const values = lines[i].split(',')
          // Check if all required values except last column are present
          const hasRequiredValues = values.slice(0, -1).every(val => val && val.trim().length > 0)
          
          if (hasRequiredValues) {
            const row = {}
            headers.forEach((header, index) => {
              // Trim value if it exists, otherwise use empty string
              row[header.trim()] = values[index] ? values[index].trim() : ''
            })
            data.push(row)
          }
        }
      }
      tableData.value = data
    }
    reader.readAsText(file)
  }
}

const deleteRow = (index) => {
  tableData.value.splice(index, 1)
}

const resetTableData = () => {
  tableData.value = []
}

const closeModal = () => {
  emit('update:visible', false)
  resetForm()
}

const resetForm = () => {
  currentUserTab.value = 0
  emailArray.value = ['']
  tableData.value = []
  selectedService.value = null
}

const sendInvitation = () => {
  if (currentUserTab.value === 0) {
    // Single invitation with service selection
    const emailsData = emailArray.value.filter(email => email && email.includes('@'))
    emit('save', {
      type: 'send_invitation',
      data: emailsData,
      serviceId: selectedService.value,
      isBulk: false
    })
  } else {
    // Bulk invitation
    // eslint-disable-next-line
    const emailsData = tableData.value.map(item => item.email)
    emit('save', {
      type: 'send_invitation',
      data: tableData.value,
      isBulk: true
    })
  }
  closeModal()
}

// Watch for modal visibility changes
watch(() => props.visible, (newVal) => {
  if (!newVal) {
    resetForm()
  }
})
</script>

<style lang="scss" scoped>
@use "../../assets/scss/variables.scss";
@use "../../assets/scss/app/common.scss";
</style>